/**
 * PackerWeightEntry — Weight entry step UI for the packer flow.
 * Renders dual layouts: desktop card grid (when embedded) and mobile stacked layout.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';
import {
  Printer,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Package,
  Wifi,
  WifiOff,
} from 'lucide-react';
import type { ScaleConnectionStatus } from '../../../hooks/useScaleWeight';
import type { EmployeeInfo, SessionInfo } from '../../../types';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode?: string;
  location_code?: string;
  printer_mac_id?: string;
}

interface PackerWeightEntryProps {
  embedded: boolean;
  session: SessionInfo;
  employee: EmployeeInfo;
  station: StationInfo;
  boxCount: number;
  expectedBoxes: number;
  weight: string;
  setWeight: (w: string) => void;
  weightNum: number;
  isWeightValid: boolean;
  isFixedWeightMode: boolean;
  isScaleMode: boolean;
  scaleWeight: number;
  scaleStatus: ScaleConnectionStatus;
  scaleIsStable: boolean;
  scaleTare: () => void;
  scaleError: string | null;
  canPrint: boolean;
  unitWeight: number;
  minWeight: number;
  maxWeight: number;
  hasSubUnits: boolean;
  subUnitsPacked: number;
  subUnitsIncomplete: boolean;
  isLoading: boolean;
  apiYield: number | null;
  handlePackSubUnit: () => void;
  handlePrintLabel: () => void;
  setShowTargetReachedModal: (v: boolean) => void;
  activeTare: number;
  grossWeight: number;
}

const PackerWeightEntry: React.FC<PackerWeightEntryProps> = ({
  embedded,
  session,
  employee,
  station,
  boxCount,
  expectedBoxes,
  weight,
  setWeight,
  weightNum,
  isWeightValid,
  isFixedWeightMode,
  isScaleMode,
  scaleWeight,
  scaleStatus,
  scaleIsStable,
  scaleTare,
  scaleError,
  canPrint,
  unitWeight,
  minWeight,
  maxWeight,
  hasSubUnits,
  subUnitsPacked,
  subUnitsIncomplete,
  isLoading,
  apiYield,
  handlePackSubUnit,
  handlePrintLabel,
  setShowTargetReachedModal,
  activeTare,
  grossWeight,
}) => {
  const { t } = useLanguage();
  const derivedSubUnitWeight = session?.sub_unit_weight && parseFloat(String(session.sub_unit_weight)) > 0
    ? parseFloat(String(session.sub_unit_weight))
    : (unitWeight && session?.item_sub_quantity
        ? unitWeight / parseFloat(String(session.item_sub_quantity))
        : 0);
  const fixedWeightDisplay = hasSubUnits && subUnitsIncomplete
    ? derivedSubUnitWeight
    : minWeight;

  // Scale mode: determine border color based on net weight vs target tolerance
  const netWeight = weightNum; // already tare-subtracted
  const getScaleBorderColor = () => {
    if (scaleStatus !== 'connected' || grossWeight === 0) return 'border-gray-300 dark:border-gray-600 text-gray-400';
    if (netWeight < minWeight || netWeight > maxWeight) return 'border-red-500 text-red-600 dark:text-red-400';
    const yellowThreshold = minWeight + (maxWeight - minWeight) * 0.8;
    if (netWeight >= yellowThreshold) return 'border-yellow-500 text-yellow-600 dark:text-yellow-400';
    return 'border-green-500 text-green-600 dark:text-green-400';
  };

  // Scale mode rendering block (shared between desktop and mobile)
  const renderScaleWeightDisplay = (sizeClasses: { text: string; py: string; lbs: string; icon: string }) => (
    <>
      <div className="text-center mb-2">
        <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
          {t('mobile.scaleWeight') || 'Scale Weight'}
        </span>
      </div>
      {/* Connection status */}
      <div className="flex items-center justify-center gap-2 mb-2">
        {scaleStatus === 'connected' ? (
          <Wifi className={`${sizeClasses.icon} text-green-500`} />
        ) : (
          <WifiOff className={`${sizeClasses.icon} text-red-500`} />
        )}
        <span className={`text-xs font-medium ${
          scaleStatus === 'connected'
            ? 'text-green-600 dark:text-green-400'
            : scaleStatus === 'connecting'
              ? 'text-yellow-600 dark:text-yellow-400'
              : 'text-red-600 dark:text-red-400'
        }`}>
          {scaleStatus === 'connected'
            ? (t('operator.scaleConnected') || 'Connected')
            : scaleStatus === 'connecting'
              ? (t('operator.scaleConnecting') || 'Connecting...')
              : (t('operator.scaleDisconnected') || 'Disconnected')}
        </span>
        {scaleStatus === 'connected' && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            scaleIsStable
              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
          }`}>
            {scaleIsStable
              ? (t('operator.scaleWeightStable') || 'Stable')
              : (t('operator.scaleWeightUnstable') || 'Unstable')}
          </span>
        )}
      </div>
      {/* Weight display — read-only, color-coded (shows net weight) */}
      <div className="relative">
        <div className={`w-full text-center ${sizeClasses.text} font-bold ${sizeClasses.py} px-4 rounded-2xl border-4 bg-gray-50 dark:bg-gray-800 ${getScaleBorderColor()}`}>
          {scaleStatus === 'connected' ? netWeight.toFixed(2) : '--'}
        </div>
        <span className={`absolute right-4 top-1/2 -translate-y-1/2 ${sizeClasses.lbs} text-gray-400 font-medium`}>lbs</span>
      </div>
      {/* Tare breakdown line */}
      {activeTare > 0 && scaleStatus === 'connected' && grossWeight > 0 && (
        <p className="text-center text-xs text-secondary-500 dark:text-gray-400 mt-1">
          {t('admin.tareGross') || 'Gross'}: {grossWeight.toFixed(2)} &minus; {t('admin.tareTare') || 'Tare'}: {activeTare.toFixed(2)} = {t('admin.tareNet') || 'Net'}: {netWeight.toFixed(2)} lbs
        </p>
      )}
      {/* Status messages */}
      <div className="mt-3 text-center">
        <p className="text-xs text-gray-400 mb-1">
          {t('mobile.weightWindow') || 'Weight Window'}: {minWeight.toFixed(2)} - {maxWeight.toFixed(2)} lbs
        </p>
        {scaleStatus !== 'connected' ? (
          <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
            <WifiOff className={sizeClasses.icon} />
            {t('mobile.scaleNotConnected') || 'Scale is not connected'}
          </p>
        ) : grossWeight === 0 ? (
          <p className="text-gray-500">{t('mobile.scaleWeightFromScale') || 'Live reading from station scale'}</p>
        ) : !scaleIsStable ? (
          <p className="text-yellow-600 dark:text-yellow-400 flex items-center justify-center gap-2">
            <Loader2 className={`${sizeClasses.icon} animate-spin`} />
            {t('mobile.waitingForStableWeight') || 'Waiting for stable weight...'}
          </p>
        ) : isWeightValid ? (
          <p className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
            <CheckCircle className={sizeClasses.icon} />
            {subUnitsIncomplete
              ? (t('mobile.readyToPackSubUnit') || 'Ready to pack sub-unit')
              : (t('mobile.readyToPrint') || 'Ready to print')}
          </p>
        ) : weightNum < minWeight ? (
          <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
            <AlertTriangle className={sizeClasses.icon} />
            {t('mobile.weightTooLow') || 'Weight is too low'}
          </p>
        ) : (
          <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
            <AlertTriangle className={sizeClasses.icon} />
            {t('mobile.weightTooHigh') || 'Weight is too high'}
          </p>
        )}
      </div>
    </>
  );

  if (embedded) {
    /* ===== DESKTOP DASHBOARD CARD LAYOUT ===== */
    return (
      <div className="flex-1 grid grid-cols-3 gap-4">
        {/* Left column: Progress card + Packing Info card */}
        <div className="col-span-1 flex flex-col gap-4">
          {/* Progress Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{station.station_name}</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{session.sub_session_number}</span>
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">{employee.display_name}</span>
              <span className="text-sm text-gray-400 dark:text-gray-500">#{employee.employee_number}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">{t('mobile.progress') || 'Progress'}</span>
              <span className={`text-sm font-medium ${
                boxCount >= expectedBoxes
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-900 dark:text-white'
              }`}>
                {boxCount} / {expectedBoxes} {t('mobile.boxes') || 'boxes'}
              </span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  boxCount >= expectedBoxes
                    ? 'bg-green-500'
                    : 'bg-amber-500'
                }`}
                style={{
                  width: `${expectedBoxes > 0
                    ? Math.min(100, (boxCount / expectedBoxes) * 100)
                    : (boxCount >= expectedBoxes ? 100 : 0)}%`
                }}
              />
            </div>
            {boxCount >= expectedBoxes && (
              <button
                onClick={() => setShowTargetReachedModal(true)}
                className="w-full mt-3 py-2 bg-green-600 text-white rounded-lg font-bold text-sm active:bg-green-700"
              >
                {t('mobile.finish') || 'Finish'}
              </button>
            )}
          </div>

          {/* Packing Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('mobile.packingInstructions') || 'Packing Instructions'}
              </h3>
            </div>
            {/* Finished Product */}
            <div className="space-y-2 text-sm">
              <div className="text-xs font-semibold uppercase text-amber-600 dark:text-amber-400">Finished Product</div>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <span className="text-gray-500">Item #:</span>
                <span className="font-bold text-gray-900 dark:text-white">{session.product_sku}</span>
                <span className="text-gray-500">Name:</span>
                <span className="text-gray-900 dark:text-white truncate">{session.product_name}{session.product_chinese ? ` (${session.product_chinese})` : ''}</span>
                {session.packing && (
                  <>
                    <span className="text-gray-500">Packing:</span>
                    <span className="text-gray-900 dark:text-white">{session.packing}</span>
                  </>
                )}
                {expectedBoxes > 0 && (
                  <>
                    <span className="text-gray-500">Expected:</span>
                    <span className="font-bold text-amber-600 dark:text-amber-400">{expectedBoxes} boxes</span>
                  </>
                )}
              </div>
            </div>
            {/* Divider */}
            <div className="my-3 border-t border-gray-200 dark:border-gray-700" />
            {/* Raw Material */}
            <div className="space-y-2 text-sm">
              <div className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400">Raw Material</div>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <span className="text-gray-500">Item #:</span>
                <span className="font-medium text-gray-900 dark:text-white">{session.raw_material_item_number || session.lot_number}</span>
                {(session.raw_material_english || session.raw_material_name) && (
                  <>
                    <span className="text-gray-500">Name:</span>
                    <span className="text-gray-900 dark:text-white truncate">{session.raw_material_english || session.raw_material_name}{session.raw_material_chinese ? ` (${session.raw_material_chinese})` : ''}</span>
                  </>
                )}
                {session.raw_material_brand && (
                  <>
                    <span className="text-gray-500">Brand:</span>
                    <span className="text-gray-900 dark:text-white">{session.raw_material_brand}</span>
                  </>
                )}
                <span className="text-gray-500">Lot:</span>
                <span className="text-gray-900 dark:text-white">{session.lot_number ? `WT ${session.lot_number}` : '-'}</span>
                {unitWeight > 0 && (
                  <>
                    <span className="text-gray-500">Unit Wt:</span>
                    <span className="text-gray-900 dark:text-white">{Number(unitWeight || 0).toFixed(2)} lbs/box</span>
                  </>
                )}
                {session.initial_material_weight && session.initial_material_weight > 0 && (
                  <>
                    <span className="text-gray-500">Initial:</span>
                    <span className="text-gray-900 dark:text-white">{Number(session.initial_material_weight || 0).toFixed(2)} lbs</span>
                  </>
                )}
                {apiYield !== null && apiYield > 0 && (
                  <>
                    <span className="text-gray-500">Yield:</span>
                    <span className="text-gray-900 dark:text-white">{formatYield(apiYield)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Weight + Print card (spans 2 cols) */}
        <div className="col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 flex flex-col items-center justify-center">
          <div className="w-full max-w-lg">
            {isScaleMode ? (
              renderScaleWeightDisplay({ text: 'text-6xl md:text-7xl', py: 'py-6', lbs: 'text-2xl', icon: 'w-4 h-4' })
            ) : isFixedWeightMode ? (
              <>
                <div className="text-center mb-2">
                  <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                    {t('mobile.fixedWeight') || 'Fixed Weight'}
                  </span>
                </div>
                <div className="relative">
                  <div className="w-full text-center text-6xl md:text-7xl font-bold py-6 px-4 rounded-2xl border-4 bg-gray-50 dark:bg-gray-800 border-green-500 text-green-600 dark:text-green-400">
                    {fixedWeightDisplay > 0 ? fixedWeightDisplay.toFixed(2) : '--'}
                  </div>
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400 font-medium">lbs</span>
                </div>
                <div className="mt-3 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('mobile.fixedWeightFromProduct') || 'From product configuration'}
                  </p>
                  {isWeightValid && (
                    <p className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2 mt-1">
                      <CheckCircle className="w-4 h-4" />
                      {subUnitsIncomplete
                        ? (t('mobile.readyToPackSubUnit') || 'Ready to pack sub-unit')
                        : (t('mobile.readyToPrint') || 'Ready to print')}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-2">
                  <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                    {t('mobile.catchWeight') || 'Catch Weight'}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    inputMode="decimal"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="0.0"
                    className={`w-full text-center text-6xl md:text-7xl font-bold py-6 px-4 rounded-2xl border-4 bg-white dark:bg-gray-800 outline-none transition-colors ${
                      weight === ''
                        ? 'border-gray-300 dark:border-gray-600 text-gray-400'
                        : isWeightValid
                          ? 'border-green-500 text-green-600 dark:text-green-400'
                          : 'border-red-500 text-red-600 dark:text-red-400'
                    }`}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400 font-medium">lbs</span>
                </div>
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">
                    {t('mobile.weightWindow') || 'Weight Window'}: {minWeight.toFixed(2)} - {maxWeight.toFixed(2)} lbs
                  </p>
                  {weight === '' ? (
                    <p className="text-gray-500">{t('mobile.enterWeight') || 'Enter box weight'}</p>
                  ) : isWeightValid ? (
                    <p className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {t('mobile.weightValid') || 'Weight is within range'}
                    </p>
                  ) : weightNum < minWeight ? (
                    <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {t('mobile.weightTooLow') || 'Weight is too low'}
                    </p>
                  ) : (
                    <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {t('mobile.weightTooHigh') || 'Weight is too high'}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          {/* Sub-unit counter button — shown only while sub-units are incomplete */}
          {hasSubUnits && subUnitsIncomplete && (
            <button
              onClick={handlePackSubUnit}
              disabled={!canPrint || weightNum <= 0}
              className={`w-full max-w-lg mt-6 py-4 rounded-xl font-bold text-lg transition-all ${
                !canPrint || weightNum <= 0
                  ? 'bg-green-600/40 text-white/70 cursor-not-allowed'
                  : subUnitsPacked === 0
                    ? 'bg-yellow-600 hover:bg-yellow-500 text-white'
                    : subUnitsPacked < session.item_sub_quantity! / 2
                      ? 'bg-yellow-500 hover:bg-yellow-400 text-white'
                      : 'bg-lime-500 hover:bg-lime-400 text-white'
              }`}
            >
              {t('mobile.packSubUnit') || 'Pack sub-unit'} {subUnitsPacked} / {Math.floor(session.item_sub_quantity!)}
            </button>
          )}

          {/* Print Button — shown when no sub-units or sub-units are complete */}
          {(!hasSubUnits || !subUnitsIncomplete) && (
          <button
            onClick={handlePrintLabel}
            disabled={!canPrint || isLoading}
            className={`w-full max-w-lg mt-3 py-4 rounded-xl font-bold text-xl flex items-center justify-center gap-3 transition-all ${
              canPrint
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <Printer className="w-6 h-6" />
                {t('mobile.printLabel') || 'Print Label'}
              </>
            )}
          </button>
          )}
        </div>
      </div>
    );
  }

  /* ===== MOBILE STACKED LAYOUT ===== */
  return (
    <div className="flex-1 flex flex-col pb-6">
      {/* Progress Bar - Box Count (with Finish button when target reached) */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl p-4 md:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">{t('mobile.progress') || 'Progress'}</span>
            <span className={`text-sm font-medium ${
              boxCount >= expectedBoxes
                ? 'text-green-600 dark:text-green-400'
                : 'text-gray-900 dark:text-white'
            }`}>
              {boxCount} / {expectedBoxes} {t('mobile.boxes') || 'boxes'}
            </span>
          </div>
          <div className="h-2 md:h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                boxCount >= expectedBoxes
                  ? 'bg-green-500'
                  : 'bg-amber-500'
              }`}
              style={{
                width: `${expectedBoxes > 0
                  ? Math.min(100, (boxCount / expectedBoxes) * 100)
                  : (boxCount >= expectedBoxes ? 100 : 0)}%`
              }}
            />
          </div>
        </div>
        {/* Finish button - appears when target box count is reached */}
        {boxCount >= expectedBoxes && (
          <button
            onClick={() => setShowTargetReachedModal(true)}
            className="self-stretch px-5 bg-green-600 text-white rounded-xl font-bold text-lg shadow-sm active:bg-green-700"
          >
            {t('mobile.finish') || 'Finish'}
          </button>
        )}
      </div>

      {/* Weight Display - Different UI for Fixed vs Catch vs Scale weight modes */}
      <div className="flex-1 flex flex-col items-center justify-center mb-4">
        <div className="w-full max-w-xs md:max-w-sm lg:max-w-md">
          {isScaleMode ? (
            /* SCALE WEIGHT MODE: Live reading from station scale */
            renderScaleWeightDisplay({ text: 'text-6xl md:text-7xl lg:text-8xl', py: 'py-8 md:py-8 lg:py-10', lbs: 'text-2xl md:text-3xl', icon: 'w-5 h-5' })
          ) : isFixedWeightMode ? (
            /* FIXED WEIGHT MODE: Read-only display of product unit_weight */
            <>
              <div className="text-center mb-2">
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                  {t('mobile.fixedWeight') || 'Fixed Weight'}
                </span>
              </div>
              <div className="relative">
                <div
                  className="w-full text-center text-6xl md:text-7xl lg:text-8xl font-bold py-8 md:py-8 lg:py-10 px-4 rounded-2xl border-4 bg-gray-50 dark:bg-gray-800 border-green-500 text-green-600 dark:text-green-400"
                >
                  {fixedWeightDisplay > 0 ? fixedWeightDisplay.toFixed(2) : '--'}
                </div>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl md:text-3xl text-gray-400 font-medium">
                  lbs
                </span>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('mobile.fixedWeightFromProduct') || 'From product configuration'}
                </p>
                {isWeightValid && (
                  <p className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2 mt-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('mobile.readyToPrint') || 'Ready to print'}
                  </p>
                )}
              </div>
            </>
          ) : (
            /* CATCH WEIGHT MODE: Manual weight entry */
            <>
              <div className="text-center mb-2">
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                  {t('mobile.catchWeight') || 'Catch Weight'}
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="0.0"
                  className={`w-full text-center text-6xl md:text-7xl lg:text-8xl font-bold py-8 md:py-8 lg:py-10 px-4 rounded-2xl border-4 bg-white dark:bg-gray-800 outline-none transition-colors ${
                    weight === ''
                      ? 'border-gray-300 dark:border-gray-600 text-gray-400'
                      : isWeightValid
                        ? 'border-green-500 text-green-600 dark:text-green-400'
                        : 'border-red-500 text-red-600 dark:text-red-400'
                  }`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl md:text-3xl text-gray-400 font-medium">
                  lbs
                </span>
              </div>
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-400 mb-1">
                  {t('mobile.weightWindow') || 'Weight Window'}: {minWeight.toFixed(2)} - {maxWeight.toFixed(2)} lbs
                </p>
                {weight === '' ? (
                  <p className="text-gray-500">{t('mobile.enterWeight') || 'Enter box weight'}</p>
                ) : isWeightValid ? (
                  <p className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('mobile.weightValid') || 'Weight is within range'}
                  </p>
                ) : weightNum < minWeight ? (
                  <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {t('mobile.weightTooLow') || 'Weight is too low'}
                  </p>
                ) : (
                  <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {t('mobile.weightTooHigh') || 'Weight is too high'}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sub-unit counter button — shown only while sub-units are incomplete */}
      {hasSubUnits && subUnitsIncomplete && (
        <button
          onClick={handlePackSubUnit}
          disabled={!canPrint || weightNum <= 0}
          className={`w-full py-5 md:py-6 rounded-2xl font-bold text-xl md:text-2xl flex items-center justify-center gap-3 transition-all ${
            !canPrint || weightNum <= 0
              ? 'bg-green-600/40 text-white/70 cursor-not-allowed'
              : subUnitsPacked === 0
                ? 'bg-yellow-600 hover:bg-yellow-500 text-white'
                : subUnitsPacked < session.item_sub_quantity! / 2
                  ? 'bg-yellow-500 hover:bg-yellow-400 text-white'
                  : 'bg-lime-500 hover:bg-lime-400 text-white'
          }`}
        >
          {t('mobile.packSubUnit') || 'Pack sub-unit'} {subUnitsPacked} / {Math.floor(session.item_sub_quantity!)}
        </button>
      )}

      {/* Print Button — shown when no sub-units or sub-units are complete */}
      {(!hasSubUnits || !subUnitsIncomplete) && (
      <button
        onClick={handlePrintLabel}
        disabled={!canPrint || isLoading}
        className={`w-full mt-3 py-5 md:py-6 lg:py-7 rounded-2xl font-bold text-xl md:text-2xl lg:text-3xl flex items-center justify-center gap-3 transition-all ${
          canPrint
            ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
            : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isLoading ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : (
          <>
            <Printer className="w-6 h-6 md:w-7 md:h-7" />
            {t('mobile.printLabel') || 'Print Label'}
          </>
        )}
      </button>
      )}

    </div>
  );
};

export default PackerWeightEntry;
